// HTTP API client for GMSomeday.
//
// All functions use relative URLs (/api/…) so the same-origin nginx/ingress
// proxy handles routing in every environment. The browser's credentials
// (cookies) travel with each request via `credentials: 'include'`.
//
// Endpoints mirror the FastAPI backend. Each function throws an Error with
// the server's `detail.hint` or `detail.code` when the response is not ok.

const DEFAULT_CREDENTIALS = 'include'

async function _json(r) {
  let body = null
  try {
    body = await r.json()
  } catch {
    // Non-JSON body (e.g. 204, empty 5xx) — fall through.
  }
  return body
}

function _errorMessage(body, status) {
  if (body && body.detail) {
    if (typeof body.detail === 'object') {
      return body.detail.hint || body.detail.code || `HTTP ${status}`
    }
    return String(body.detail)
  }
  return `HTTP ${status}`
}

function _httpError(body, status) {
  const error = new Error(_errorMessage(body, status))
  const detail = body?.detail
  error.status = status
  error.code = typeof detail === 'object' && detail ? detail.code || '' : ''
  error.detail = detail
  return error
}

async function _get(path) {
  const r = await fetch(path, { credentials: DEFAULT_CREDENTIALS })
  if (!r.ok) {
    const body = await _json(r)
    throw _httpError(body, r.status)
  }
  return _json(r)
}

async function _post(path, payload) {
  const options = {
    method: 'POST',
    credentials: DEFAULT_CREDENTIALS,
  }
  if (payload !== undefined) {
    options.headers = { 'Content-Type': 'application/json' }
    options.body = JSON.stringify(payload)
  }
  const r = await fetch(path, options)
  if (!r.ok) {
    const body = await _json(r)
    throw _httpError(body, r.status)
  }
  return _json(r)
}

async function _delete(path) {
  const r = await fetch(path, {
    method: 'DELETE',
    credentials: DEFAULT_CREDENTIALS,
  })
  if (!r.ok) {
    const body = await _json(r)
    throw _httpError(body, r.status)
  }
  return _json(r)
}

function _query(params) {
  const q = new URLSearchParams()
  for (const [key, value] of Object.entries(params || {})) {
    if (value !== undefined && value !== null && value !== '') q.set(key, value)
  }
  const s = q.toString()
  return s ? `?${s}` : ''
}

export function getDashboard() {
  return _get('/api/dashboard')
}

export function getAnalyses() {
  return _get('/api/analysis')
}

export function getAnalysis(analysisId) {
  return _get(`/api/analysis/${encodeURIComponent(analysisId)}`)
}

export function resolveAnalysis(platform, username) {
  return _get(`/api/analysis/resolve${_query({ platform, username })}`)
}

export function admitReportRefresh(platform, username) {
  return _post(
    `/api/r/${encodeURIComponent(platform)}/${encodeURIComponent(username)}/refresh`,
    {},
  )
}

export function createPracticeFromAnalysis(analysisId, recommendationRef) {
  return _post(`/api/analysis/${encodeURIComponent(analysisId)}/practice`, recommendationRef)
}

export function getPracticeList() {
  return _get('/api/practice')
}

export function getPractice(practiceId) {
  return _get(`/api/practice/${encodeURIComponent(practiceId)}`)
}

export function submitPracticeAttempt(practiceId, positionIndex, attemptedMove) {
  return _post(`/api/practice/${encodeURIComponent(practiceId)}/attempt`, {
    position_index: positionIndex,
    attempted_move: attemptedMove,
  })
}

export function completePractice(practiceId) {
  return _post(`/api/practice/${encodeURIComponent(practiceId)}/complete`)
}

export function getProgress() {
  return _get('/api/progress')
}

/**
 * Current user — shared with useAuth, but exposed here too for views
 * that only need a one-shot fetch without the composable's reactive
 * state. GET /api/auth/me.
 */
export function getMe() {
  return _get('/api/auth/me')
}

export function getAccountSettings() {
  return _get('/api/account')
}

export function exportAccountData() {
  return _get('/api/account/export')
}

export function unlinkAccountUsername(linkId) {
  return _delete(`/api/account/links/${encodeURIComponent(linkId)}`)
}

export function saveAccountLink(platform, username) {
  return _post('/api/account/links', { platform, username })
}

export function deleteAccount() {
  return _delete('/api/account')
}

export function subscribeWeeklyRescan(platform, username) {
  return _post('/api/account/rescan-subscriptions', { platform, username })
}

export function unsubscribeWeeklyRescan(subscriptionId) {
  return _delete(`/api/account/rescan-subscriptions/${encodeURIComponent(subscriptionId)}`)
}

export function startGoogleSignIn(next = '/') {
  const qs = _query({ next })
  window.location.href = `/api/auth/google/start${qs}`
}

export function sendMagicLink(email, next = '/home') {
  return _post('/api/auth/magic-link', { email, next })
}

export function pollMagicLink(requestId) {
  return _get(`/api/auth/magic-link/poll/${encodeURIComponent(requestId)}`)
}

export function logout() {
  return _post('/api/auth/logout')
}
